import { debug } from 'console'

import { dash, pick } from 'radashi'
import { useEffect } from 'react'

import { withDatadogRetries } from '../services/datadog'
import { selectJourney } from '../store/selectors/config'
import {
  selectAppParams,
  selectCurrentStep,
  selectSubmittedAt
} from '../store/selectors/runtime'

import { useAppStoreSelector } from './useAppStoreSelector'
import { usePrevious } from './usePrevious'

/**
 * This hook is used to allow tracking in Datadog depending on the setup.
 * We initially init DD with `trackingConsent: 'not-granted'` and then we set it to 'granted' if enabled.
 */
export const useTracking = () => {
  const appParams = useAppStoreSelector(selectAppParams)
  const currentStep = useAppStoreSelector(selectCurrentStep)
  const lastCurrentStep = usePrevious(currentStep)
  const submittedAt = useAppStoreSelector(selectSubmittedAt)

  /**
   * Whether coockies are enabled, or consent has been given.
   * If false, disables tracking tools like DataDog to not set non-essential cookies.
   */
  const enabled = useAppStoreSelector((state) => {
    if (selectJourney(state)) {
      return selectJourney(state)?.settings?.thirdPartyCookies !== false
    }

    return false
  })

  useEffect(() => {
    try {
      const consent = enabled ? 'granted' : 'not-granted'
      const cancel = withDatadogRetries((client) => {
        client.setTrackingConsent(consent)

        debug('[Datadog] Tracking consent set', { consent })
      })

      return cancel
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Datadog] Failed to set tracking consent', error)
    }
  }, [enabled])

  useEffect(() => {
    if (!appParams) return

    withDatadogRetries((client) => {
      client.setGlobalContextProperty('appParams', appParams)
    })
  }, [appParams])

  /**
   * Sends a view event to Datadog whenever the current Step changes,
   * indicating the user either landed on the Journey or navigated somewhere.
   *
   * Only the {@link currentStep.id} is observed and that is intentionally done to avoid
   * sending unnecessary events.
   */
  useEffect(() => {
    if (currentStep && currentStep?.id !== lastCurrentStep?.id) {
      const name = dash(currentStep.name.toLowerCase())

      withDatadogRetries((client) => {
        client.startView({
          name,
          context: {
            currentStep: pick(currentStep, ['id', 'name', 'orderIndex'])
          }
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.id])

  /**
   * Registers an action whenever the user submits the Journey.
   * This is useful for having such contextual information within DD in case of
   * debugging or understanding the breadcrumbs of a given session.
   */
  useEffect(() => {
    if (submittedAt) {
      withDatadogRetries((client) => {
        client.addAction('journey_submitted', {
          submittedAt: submittedAt.toISOString()
        })
      })
    }
  }, [submittedAt])

  /**
   * Registers an action whenever data injection values are set or changed,
   * therefore we'd know it happened and what it looked like at the time.
   *
   * Note: the values are already tracked via 'setGlobalContextProperty', therefore
   * there is not need to record them again.
   */
  useEffect(() => {
    if (appParams?.dataInjectionOptions) {
      withDatadogRetries((client) => {
        client.addAction('data_injection_hapenned')
      })
    }
  }, [appParams?.dataInjectionOptions])
}
